import { useEffect, useMemo, useState, useRef } from "react";
import SearchInput from "./shared/SearchInput";
import HelpButton from "./shared/HelpButton";
import { fetchTestNTag, fetchTkEmailCandidates, fetchTkCcDefault, saveTkCcDefault,
  tntReportPreview, tntReportSend } from "../utils/api";

/* ============================================================================
 * TestNTagDashboard — the Test & Tag register, by month (Unit Due), Iglu-style.
 *   · one fetch of the whole register (fetchTestNTag); month navigation
 *     ("Aug2026") filters on the Unit Due date client-side
 *   · "◄ 6 months" / "6 months ►" widen the window to the six months ending
 *     with / starting from the displayed month (backlog vs. what's coming due)
 *   · search by client, item, serial number or licence
 *   · ⚡ Due Today chip (lead OR unit due = today) + 📅 specific due dates
 *   · licence chips (one per person who has tested) — single-select filter
 *   · five clickable totals with icons that filter the table:
 *       Repair 🔧 | Ok ✓ | Overdue ⏱ | Depot ⌂ | Pending Delete ⌛
 *   · rows grouped by Unit Due with a day header; the last column shows the
 *     icon of every filter the row matches, in that filter's colour
 *   · ↻ refresh, ⚡ quick filter = Overdue, ✉ report modal (preview / email PDF)
 * ==========================================================================*/

const MONTHS3 = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/* mirror of get_testNtag_data row order (same F map as TestNTag.jsx) */
const F = {
  id: 0, leadStatus: 1, unitStatus: 2, leadDue: 3, unitDue: 4, client: 5, licence: 6,
  item: 7, itemCode: 8, condition: 9, licenceNumber: 10, leadTag: 11, unitTag: 12,
  serialNum: 13, inspectLead: 14, inspectUnit: 15, passFail: 16, comments: 17, authDelete: 18,
};
const KEYS = Object.keys(F);

/* dates arrive as dd/mm/yyyy or ISO — normalise to ISO for compare/group */
const toISO = (s) => {
  const str = String(s ?? "").trim();
  if (!str) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const [d, m, y] = str.split("/");
  return d && m && y ? `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}` : "";
};
const toObjects = (arrs) => (arrs || []).map((a) => {
  const o = {};
  KEYS.forEach((k) => { o[k] = a[F[k]]; });
  o.leadDueISO = toISO(o.leadDue);
  o.unitDueISO = toISO(o.unitDue);
  return o;
});
const fmtDate = (iso) => {
  if (!iso || !iso.includes("-")) return iso || "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const todayISO = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};

/* ---------- predicates (same rules as the TestNTag page chips) ---------- */
const eqI = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
const isRepair  = (r) => eqI(r.condition, "Repair");
const isOverdue = (r) => eqI(r.leadStatus, "Overdue") || eqI(r.unitStatus, "Overdue");
const isOk      = (r) => eqI(r.leadStatus, "Ok") && eqI(r.unitStatus, "Ok");   // both ok
const isDepot   = (r) => eqI(r.client, "Depot");
const isPending = (r) => /^\d{6}$/.test(String(r.authDelete ?? "").trim());
const isDueToday = (r) => { const t = todayISO(); return r.leadDueISO === t || r.unitDueISO === t; };

/* ---------- icons ---------- */
const WrenchIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);
const CheckCircleIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const ClockIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" />
  </svg>
);
const HomeIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />
  </svg>
);
const HourglassIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2h12" /><path d="M6 22h12" /><path d="M8 2v3.5L12 10l4-4.5V2" /><path d="M8 22v-3.5L12 14l4 4.5V22" />
  </svg>
);

/* five totals — one row; the colours are the TestNTag chip palette */
const FILTERS = [
  { key:"repair",  label:"Repair",         color:"#fbbf24", Icon:WrenchIcon,      test:isRepair,  title:"Condition is Repair" },
  { key:"ok",      label:"Ok",             color:"#4ade80", Icon:CheckCircleIcon, test:isOk,      title:"Lead and unit both Ok" },
  { key:"overdue", label:"Overdue",        color:"#f87171", Icon:ClockIcon,       test:isOverdue, title:"Lead or unit overdue", sub:isDueToday },
  { key:"depot",   label:"Depot",          color:"#60a5fa", Icon:HomeIcon,        test:isDepot,   title:"Client is Depot" },
  { key:"pending", label:"Pending Delete", color:"#fb7185", Icon:HourglassIcon,   test:isPending, title:"Awaiting delete authorisation" },
];

const S = {
  backdrop: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(2,6,23,0.7)", zIndex:1100,
    display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"40px 16px", overflowY:"auto" },
  modal: { position:"relative", maxHeight: "calc(var(--fr-modal-max, 82vh) - 76px)", overflowY: "auto", overscrollBehavior: "contain", width:"100%", maxWidth:1200,
    background:"linear-gradient(135deg,#1e293b 0%,#0f172a 100%)",
    border:"1px solid rgba(34,211,238,0.25)", borderRadius:16, padding:"18px 18px 14px" },
  accent: { position:"absolute", top:0, left:0, right:0, height:3,
    background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  head: { position: "sticky", top: 0, zIndex: 5, background: "#1e293b", boxShadow: "0 -18px 0 0 #1e293b",  display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 },
  title: { margin:0, fontSize:"1rem", fontWeight:700, color:"#f1f5f9" },
  closeX: { background:"none", border:"none", color:"rgba(255,255,255,0.6)", fontSize:"1.4rem", lineHeight:1, cursor:"pointer" },
  nav: { display:"flex", alignItems:"center", justifyContent:"center", gap:16, margin:"2px 0 10px" },
  navBtn: { width:32, height:32, borderRadius:9, border:"1px solid rgba(34,211,238,0.4)",
    background:"rgba(34,211,238,0.1)", color:"#22d3ee", cursor:"pointer", fontSize:"0.95rem", lineHeight:1 },
  navLabel: { minWidth:120, textAlign:"center", fontWeight:800, color:"#f1f5f9", fontSize:"0.95rem", letterSpacing:"0.02em" },
  /* Current — same gradient pill as HigherView's "Current" nav button */
  navCurrent: (dim) => ({ padding:"6px 12px", borderRadius:9, border:"none", fontSize:"0.72rem", fontWeight:700,
    background:"linear-gradient(135deg,#22d3ee,#3b82f6)", color:"#fff", cursor: dim ? "default" : "pointer",
    opacity: dim ? 0.45 : 1, whiteSpace:"nowrap" }),
  rangeBtn: (on) => ({ padding:"6px 10px", borderRadius:9, fontSize:"0.72rem", fontWeight:700, cursor:"pointer", whiteSpace:"nowrap",
    border:`1px solid ${on ? "rgba(139,92,246,0.7)" : "rgba(255,255,255,0.15)"}`,
    background: on ? "rgba(139,92,246,0.2)" : "rgba(255,255,255,0.03)",
    color: on ? "#c4b5fd" : "rgba(255,255,255,0.55)" }),
  chipsRow: { display:"flex", flexWrap:"wrap", gap:6, margin:"8px 0" },
  dayChip: (on) => ({ padding:"4px 12px", borderRadius:999, fontSize:"0.72rem", fontWeight:700, cursor:"pointer",
    border:`1px solid ${on ? "rgba(59,130,246,0.7)" : "rgba(255,255,255,0.15)"}`,
    background: on ? "rgba(59,130,246,0.2)" : "rgba(255,255,255,0.03)",
    color: on ? "#93c5fd" : "rgba(255,255,255,0.55)" }),
  buttonsGrid: { display:"grid", gridTemplateColumns:"repeat(5,minmax(0,1fr))", gap:10, margin:"10px 0 6px" },
  countBtn: (active, color) => ({ borderRadius:12, padding:"11px 8px", cursor:"pointer",
    background: active ? "rgba(34,211,238,0.15)" : "rgba(34,211,238,0.08)",
    border:`1px solid ${active ? color : "rgba(34,211,238,0.25)"}`,
    display:"flex", flexDirection:"column", alignItems:"center", gap:4 }),
  countLabel: { display:"inline-flex", alignItems:"center", gap:5, fontSize:"0.62rem", color:"rgba(255,255,255,0.55)",
    textTransform:"uppercase", letterSpacing:"0.05em", fontWeight:700, textAlign:"center", lineHeight:1.3 },
  countValue: (color) => ({ fontSize:"1.55rem", fontWeight:800, lineHeight:1, color }),
  countSub: { fontSize:"0.6rem", color:"rgba(255,255,255,0.45)", fontWeight:700 },
  card: { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)",
    borderRadius:12, padding:"12px", marginTop:8 },
  dateHead: { display:"flex", justifyContent:"space-between", alignItems:"baseline",
    padding:"10px 8px 6px", position:"sticky", top:0, background:"#1b2537", zIndex:1 },
  dateTitle: { fontSize:"0.78rem", fontWeight:800, color:"#22d3ee", letterSpacing:"0.04em" },
  dateMeta: { fontSize:"0.7rem", color:"rgba(255,255,255,0.45)" },
  colHead: { fontSize:"0.62rem", letterSpacing:"0.05em", textTransform:"uppercase",
    color:"rgba(255,255,255,0.45)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  cell: { fontSize:"0.82rem", color:"#e2e8f0", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  name: { fontSize:"0.84rem", fontWeight:700, color:"#22d3ee", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  badge: (ok) => ({ fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.03em", padding:"1px 7px", borderRadius:999,
    whiteSpace:"nowrap", background: ok ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.12)",
    color: ok ? "#4ade80" : "#f87171", border:`1px solid ${ok ? "rgba(74,222,128,0.35)" : "rgba(248,113,113,0.4)"}` }),
  flag: (color) => ({ width:26, height:26, borderRadius:8, display:"inline-flex", alignItems:"center", justifyContent:"center",
    color, background:`${color}1f`, border:`1px solid ${color}66`, flexShrink:0 }),
  empty: { padding:"24px", textAlign:"center", color:"rgba(255,255,255,0.5)", fontStyle:"italic", fontSize:"0.85rem" },
  closeBtn: { width:"100%", padding:"11px", borderRadius:10, border:"none",
    background:"rgba(255,255,255,0.06)", color:"#e2e8f0", fontWeight:600, cursor:"pointer", marginTop:12 },
};

/* Client | Item | Lead Status | Unit Status | Lead Due | Unit Due | flags */
const TABLE_COLS = "1.3fr 1.3fr 0.75fr 0.75fr 0.9fr 0.9fr 160px";

/* ---------- nested overlay styles (recipients + send) ---------- */
const O = {
  overlay: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(0,0,0,0.75)", backdropFilter:"blur(6px)",
    display:"flex", alignItems:"center", justifyContent:"center", zIndex:1200, padding:20 },
  modal: { background:"linear-gradient(135deg,#0f172a 0%,#1e293b 100%)", border:"1px solid rgba(34,211,238,0.25)",
    borderRadius:16, width:"100%", maxWidth:640, overflow:"hidden", boxShadow:"0 25px 80px rgba(0,0,0,0.6)" },
  bar: { height:3, background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  header: { padding:"16px 22px 8px", display:"flex", justifyContent:"space-between", alignItems:"center" },
  hTitle: { fontSize:"1rem", fontWeight:700, color:"#f1f5f9", margin:0 },
  body: { padding:"8px 22px 4px", maxHeight:"min(60vh, calc(100dvh - var(--fr-modal-top, 0px) - 200px))", overflowY:"auto" },
  footer: { padding:"14px 22px", display:"flex", justifyContent:"flex-end", gap:10,
    background:"rgba(0,0,0,0.2)", borderTop:"1px solid rgba(255,255,255,0.06)" },
  cancel: { background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.7)", border:"1px solid rgba(255,255,255,0.1)",
    borderRadius:10, padding:"10px 18px", fontSize:"0.85rem", fontWeight:600, cursor:"pointer" },
  confirm: (busy) => ({ background:"linear-gradient(135deg,#22d3ee 0%,#3b82f6 100%)", color:"#0f172a", border:"none",
    borderRadius:10, padding:"10px 20px", fontSize:"0.85rem", fontWeight:700,
    cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1, display:"inline-flex", alignItems:"center", gap:8 }),
  row: { display:"grid", gridTemplateColumns:"28px 1.2fr 1fr 1.4fr", alignItems:"center",
    columnGap:8, padding:"9px 4px", borderBottom:"1px solid rgba(255,255,255,0.06)" },
  rName: { fontSize:"0.85rem", fontWeight:700, color:"#22d3ee", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  rCell: { fontSize:"0.8rem", color:"#e2e8f0", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  pill: { fontSize:"0.72rem", color:"rgba(255,255,255,0.55)" },
  check: { width:16, height:16, accentColor:"#22d3ee", cursor:"pointer" },
  note: { padding:"8px 12px", borderRadius:8, margin:"8px 0", fontSize:"0.78rem",
    background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.35)", color:"#4ade80" },
  err: { padding:"8px 12px", borderRadius:8, margin:"8px 0", fontSize:"0.78rem",
    background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.35)", color:"#fca5a5" },
};

const Spin = () => (
  <span style={{ width:12, height:12, border:"2px solid currentColor", borderTopColor:"transparent",
    borderRadius:"50%", display:"inline-block", animation:"tnt-spin 0.7s linear infinite" }} />
);

/* ---------- send report — same recipient pool + CC-default chips as Iglu ---------- */
const SendModal = ({ filterHeader, reportRows, summary, onClose }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState("");             // "preview" | "send"
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const CC_CATEGORIES = [
    { key: "supervisor", label: "Supervisors" },
    { key: "admin",      label: "Admin" },
    { key: "operations", label: "Operations" },
  ];
  const catOf = (r) => r.category || "operations";
  const [chips, setChips] = useState([]);

  useEffect(() => {
    Promise.all([fetchTkEmailCandidates(), fetchTkCcDefault()]).then(([cand, def]) => {
      const candidates = cand.status === "success" ? cand.candidates || [] : [];
      const active = def.status === "success" && Array.isArray(def.chips) ? def.chips : ["supervisor"];
      setList(candidates);
      setChips(active);
      const pre = {};
      candidates.forEach((r) => { if (active.includes(catOf(r))) pre[r.email] = true; });
      setChecked(pre);
      setLoading(false);
    });
  }, []);

  const toggleChip = (key) => {
    const next = chips.includes(key) ? chips.filter((c) => c !== key) : [...chips, key];
    setChips(next);
    const pre = {};
    list.forEach((r) => { if (next.includes(catOf(r))) pre[r.email] = true; });
    setChecked(pre);
    saveTkCcDefault(next);
  };
  const allChecked = list.length > 0 && list.every((r) => checked[r.email]);
  const toggleAll = () => {
    const next = {};
    if (!allChecked) list.forEach((r) => { next[r.email] = true; });
    setChecked(next);
  };
  const recipients = list.filter((r) => checked[r.email]).map((r) => ({ name: r.name, email: r.email }));

  const preview = async () => {
    setBusy("preview"); setError(""); setNote("");
    const res = await tntReportPreview(filterHeader, reportRows, summary);
    if (res.status === "success") window.open(URL.createObjectURL(res.blob), "_blank");
    else setError(res.error || "Preview failed");
    setBusy("");
  };
  const send = async () => {
    setBusy("send"); setError(""); setNote("");
    const res = await tntReportSend(filterHeader, reportRows, recipients, summary);
    if (res.status === "success") {
      setNote(`Sent — ${recipients.length} personalised email${recipients.length === 1 ? "" : "s"} + work inbox copy.`);
    } else setError(res.error || "Send failed");
    setBusy("");
  };

  return (
    <div style={O.overlay} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div style={O.modal}>
        <div style={O.bar} />
        <div style={O.header}>
          <h5 style={O.hTitle}>✉ Send Test &amp; Tag Report</h5>
          <span style={O.pill}>{reportRows.length} items in filter</span>
        </div>
        <div style={O.body}>
          <div style={{ ...O.pill, margin:"2px 0 8px" }}>{filterHeader}</div>
          <div style={{ display:"flex", gap:6, margin:"4px 0 10px" }}>
            {CC_CATEGORIES.map((c) => (
              <button key={c.key} type="button" style={S.dayChip(chips.includes(c.key))} onClick={() => toggleChip(c.key)}>
                {c.label}
              </button>
            ))}
          </div>
          {note && <div style={O.note}>{note}</div>}
          {error && <div style={O.err}>{error}</div>}
          <div style={{ ...O.row, borderBottom:"1px solid rgba(255,255,255,0.12)" }}>
            <input type="checkbox" style={O.check} checked={allChecked} onChange={toggleAll} title="Select all" />
            {["Name", "Job", "Email"].map((h) => (
              <span key={h} style={{ fontSize:"0.62rem", textTransform:"uppercase", letterSpacing:"0.05em",
                color:"rgba(255,255,255,0.45)", fontWeight:700 }}>{h}</span>
            ))}
          </div>
          {loading ? (
            <div style={{ padding:20, textAlign:"center", color:"rgba(255,255,255,0.5)" }}>Loading…</div>
          ) : list.map((r) => (
            <div key={r.email} style={O.row}>
              <input type="checkbox" style={O.check} checked={!!checked[r.email]}
                onChange={() => setChecked((p) => ({ ...p, [r.email]: !p[r.email] }))} />
              <span style={O.rName}>{r.name}</span>
              <span style={O.rCell}>{r.job || "—"}</span>
              <span style={O.rCell}>{r.email}</span>
            </div>
          ))}
        </div>
        <div style={O.footer}>
          <button type="button" style={O.cancel} onClick={onClose}>Close</button>
          <button type="button" style={O.confirm(busy === "preview")} onClick={preview} disabled={!!busy || reportRows.length === 0}>
            {busy === "preview" ? <Spin /> : "👁"} Preview Report
          </button>
          <button type="button" style={O.confirm(busy === "send")} onClick={send} disabled={!!busy || reportRows.length === 0}>
            {busy === "send" ? <Spin /> : "✉"} Send Emails ({recipients.length})
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================ */
const TestNTagDashboard = ({ onClose, refreshKey = 0, onBusy }) => {
  const inline = !onClose;                    // rendered inside a tab, not as a modal
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());          // 0-11
  const [year, setYear] = useState(now.getFullYear());
  const [all, setAll] = useState([]);                          // whole register
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);         // ↻ soft refresh
  const [query, setQuery] = useState("");
  const [dueTodayOnly, setDueTodayOnly] = useState(false);     // ⚡ chip
  const [filters, setFilters] = useState([]);                  // total buttons
  const [sendOpen, setSendOpen] = useState(false);
  const [dates, setDates] = useState([]);                      // selected ISO due dates
  const [licenceSel, setLicenceSel] = useState("");             // licence chip; "" = everyone
  const dateRef = useRef(null);
  const [range, setRange] = useState(null);                    // null | "prev6" | "next6"

  /* the window of months in view: one month, or six ending with / starting
     from the displayed month. Each entry is a "YYYY-MM" prefix. */
  const window_ = useMemo(() => {
    const n = range ? 6 : 1;
    const startOffset = range === "prev6" ? -5 : 0;
    const out = [];
    for (let i = 0; i < n; i++) {
      const d = new Date(year, month + startOffset + i, 1);
      out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return out;
  }, [year, month, range]);
  const firstPrefix = window_[0];
  const lastPrefix = window_[window_.length - 1];
  const [ly, lm] = lastPrefix.split("-").map(Number);
  const lastDay = new Date(ly, lm, 0).getDate();
  const dateMin = `${firstPrefix}-01`;
  const dateMax = `${lastPrefix}-${String(lastDay).padStart(2, "0")}`;
  const labelOf = (prefix) => { const [y, m] = prefix.split("-").map(Number); return `${MONTHS3[m - 1]} ${y}`; };
  const windowLabel = range ? `${labelOf(firstPrefix)} – ${labelOf(lastPrefix)}` : labelOf(firstPrefix);

  const load = async () => {
    const res = await fetchTestNTag();
    return toObjects(res?.testntag);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try { const rows = await load(); if (alive) setAll(rows); }
      catch { if (alive) setAll([]); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  /* soft refresh — refetch in place, keep month / filters / chips / search */
  const refresh = async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    try { setAll(await load()); } catch { /* keep current rows */ }
    setRefreshing(false);
  };
  useEffect(() => { if (refreshKey) refresh(); }, [refreshKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onBusy?.(loading || refreshing); }, [loading, refreshing, onBusy]);

  useEffect(() => {
    if (inline) return;                       // no Escape-to-close inside a tab
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, inline]);

  const nav = (d) => {
    let m = month + d, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y); setFilters([]); setDates([]);
  };

  /* Current — snap back to today's month and drop any 6-month window;
     the ◄ ► arrows deliberately keep the window so it can be slid along */
  const isCurrent = !range && month === now.getMonth() && year === now.getFullYear();
  const goCurrent = () => {
    setMonth(now.getMonth()); setYear(now.getFullYear());
    setRange(null); setFilters([]); setDates([]);
  };

  /* ⚡ jump: everything overdue in the month */
  const overduePreset = () => {
    setFilters(["overdue"]);
    setDates([]); setDueTodayOnly(false); setQuery("");
  };

  /* month scope: Unit Due falls inside the window (one month or six) */
  const rows = useMemo(() => all.filter((r) => window_.includes(r.unitDueISO.slice(0, 7))), [all, window_]);

  /* licence chips — one per person who has conducted a test, first name shown */
  const licenceChips = useMemo(() => {
    const seen = new Map();
    all.forEach((r) => {
      const lic = String(r.licence ?? "").trim();
      if (lic && !seen.has(lic)) seen.set(lic, lic.split(/\s+/)[0]);
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [all]);

  /* chips + search scope (totals compute on this) */
  const scoped = useMemo(() => {
    const q = query.toLowerCase().replace(/\s+/g, "");
    return rows.filter((r) => {
      if (licenceSel && String(r.licence ?? "").trim() !== licenceSel) return false;
      if (dates.length && !dates.includes(r.leadDueISO) && !dates.includes(r.unitDueISO)) return false;
      if (dueTodayOnly && !isDueToday(r)) return false;
      if (q) {
        const hay = `${r.client}${r.item}${r.serialNum}${r.licence}${r.itemCode}`.toLowerCase().replace(/\s+/g, "");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, licenceSel, dates, dueTodayOnly, query]);

  const counts = useMemo(() => {
    const c = {}, sub = {};
    FILTERS.forEach((f) => {
      const hit = scoped.filter(f.test);
      c[f.key] = hit.length;
      if (f.sub) sub[f.key] = hit.filter(f.sub).length;
    });
    return { c, sub };
  }, [scoped]);

  const visible = useMemo(() => {
    if (!filters.length) return scoped;
    const active = FILTERS.filter((f) => filters.includes(f.key));
    return scoped.filter((r) => active.some((f) => f.test(r)));
  }, [scoped, filters]);

  /* group by Unit Due, ascending */
  const groups = useMemo(() => {
    const g = {};
    visible.forEach((r) => { (g[r.unitDueISO] = g[r.unitDueISO] || []).push(r); });
    return Object.keys(g).sort().map((d) => ({
      date: d,
      dueToday: g[d].filter(isDueToday).length,
      rows: g[d].sort((a, b) => String(a.client || "").localeCompare(String(b.client || "")) ||
        String(a.item || "").localeCompare(String(b.item || ""))),
    }));
  }, [visible]);

  const summary = useMemo(() => ({
    totalItems: scoped.length,
    dueToday: scoped.filter(isDueToday).length,
    counts: counts.c,
  }), [scoped, counts]);

  const filterHeader = useMemo(() => {
    const parts = [`${windowLabel} (Unit Due)`];
    if (licenceSel) parts.push(`Licence: ${licenceSel}`);
    if (dueTodayOnly) parts.push("Due today only");
    if (dates.length) parts.push(`Due Dates: ${dates.map(fmtDate).join(", ")}`);
    if (query.trim()) parts.push(`Search: "${query.trim()}"`);
    const act = FILTERS.filter((x) => filters.includes(x.key)).map((x) => x.label);
    parts.push(act.length ? `Filter: ${act.join(" + ")}` : "Filter: All items");
    return parts.join(" · ");
  }, [windowLabel, licenceSel, dueTodayOnly, dates, query, filters]);

  /* rows serialised for the PDF backend — named fields, every flag the row
     carries, plus which of the ACTIVE filter buttons it matched */
  const reportRows = useMemo(() => {
    const active = FILTERS.filter((f) => filters.includes(f.key));
    return visible.map((r) => ({
      "Client": r.client, "Item": r.item, "Serial Number": r.serialNum, "Item Code": r.itemCode,
      "Condition": r.condition, "Licence": r.licence,
      "Lead Status": r.leadStatus, "Unit Status": r.unitStatus,
      "Lead Due": r.leadDueISO, "Unit Due": r.unitDueISO,
      "Due Today": isDueToday(r) ? "Yes" : "No",
      "Flags": FILTERS.filter((f) => f.test(r)).map((f) => f.label),
      "Matched": active.filter((f) => f.test(r)).map((f) => f.label),
    }));
  }, [visible, filters]);

  const shell = (
    <>
      <style>{`@keyframes tnt-spin{to{transform:rotate(360deg)}}
        .tnt-row{transition:background .12s ease}.tnt-row:hover{background:rgba(34,211,238,0.06)}`}</style>
      <div style={{ ...S.modal, margin: inline ? "0 auto" : undefined }} onClick={(e) => e.stopPropagation()}>
        <div style={S.accent} />
        <div style={S.head}>
          <h4 style={S.title}>Test &amp; Tag Dashboard</h4>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <HelpButton helpData={[
              { chip: 'testntagdashboard', urlFilter: '/static/pdffilters/TestNTagDashboard.pdf' }
            ]} />
            <button type="button" style={{ ...S.navBtn, opacity: refreshing ? 0.6 : 1 }}
              title="Refresh data — keeps current filters" onClick={refresh}>
              {refreshing ? <Spin /> : "↻"}
            </button>
            <button type="button" style={S.navBtn} title="Quick filter — all overdue items" onClick={overduePreset}>⚡</button>
            <button type="button" style={S.navBtn} title="Send report for the current filter" onClick={() => setSendOpen(true)}>✉</button>
            {!inline && (
              <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
            )}
          </div>
        </div>

        {/* month navigation — Unit Due */}
        {/* tier 1 — mode: ◄ 6 months | Current | 6 months ► (active mode stays lit) */}
        <div style={{ ...S.nav, margin:"2px 0 8px", gap:20 }}>
          <button type="button" style={S.rangeBtn(range === "prev6")}
            title="Show the six months ending with the displayed month"
            onClick={() => setRange((r) => (r === "prev6" ? null : "prev6"))}>◄ 6 months</button>
          <button type="button" style={S.navCurrent(isCurrent)} disabled={isCurrent}
            title="Back to the current month, month by month" onClick={goCurrent}>Current</button>
          <button type="button" style={S.rangeBtn(range === "next6")}
            title="Show the six months starting from the displayed month"
            onClick={() => setRange((r) => (r === "next6" ? null : "next6"))}>6 months ►</button>
        </div>
        {/* tier 2 — navigation + what's in view */}
        <div style={S.nav}>
          <button type="button" style={S.navBtn} onClick={() => nav(-1)}>◄</button>
          <span style={{ ...S.navLabel, minWidth: range ? 200 : 120 }}>{windowLabel}</span>
          <button type="button" style={S.navBtn} onClick={() => nav(1)}>►</button>
        </div>

        <SearchInput value={query} onChange={setQuery} placeholder="Search client, item, serial or licence…" />

        {/* ⚡ due-today chip + specific due dates (lead OR unit) */}
        <div style={S.chipsRow}>
          <button type="button" style={S.dayChip(dueTodayOnly)} title="Lead or unit due today"
            onClick={() => setDueTodayOnly((p) => !p)}>
            ⚡ Due Today
          </button>
          <button type="button" style={S.dayChip(dates.length > 0)} title="Filter specific due dates (lead or unit)"
            onClick={() => dateRef.current?.showPicker?.() || dateRef.current?.click()}>
            📅
          </button>
          <input ref={dateRef} type="date" min={dateMin} max={dateMax}
            style={{ position:"absolute", opacity:0, pointerEvents:"none", width:0, height:0 }}
            onChange={(e) => {
              const v = e.target.value;
              if (v) setDates((p) => (p.includes(v) ? p : [...p, v]));
              e.target.value = "";
            }} />
          {dates.map((d) => (
            <button key={d} type="button" style={S.dayChip(true)} title="Remove"
              onClick={() => setDates((p) => p.filter((x) => x !== d))}>
              {fmtDate(d)} ×
            </button>
          ))}
          {/* licence chips — who conducted the test; one at a time, click again to clear */}
          {licenceChips.map((c, i) => {
            const on = licenceSel === c.value;
            return (
              <button key={c.value} type="button" title={c.value}
                style={{ ...S.dayChip(on), marginLeft: i === 0 ? 20 : 0,
                  ...(on ? { background:"rgba(34,211,238,0.15)", color:"#22d3ee", borderColor:"rgba(34,211,238,0.5)" } : null) }}
                onClick={() => setLicenceSel(on ? "" : c.value)}>
                {c.label}
              </button>
            );
          })}
        </div>

        {/* five totals — clickable filters with icons */}
        <div style={S.buttonsGrid}>
          {FILTERS.map((f) => {
            const active = filters.includes(f.key);
            return (
              <button key={f.key} type="button" style={S.countBtn(active, f.color)} title={f.title}
                onClick={() => setFilters((p) => p.includes(f.key) ? p.filter((x) => x !== f.key) : [...p, f.key])}>
                <span style={S.countLabel}><span style={{ color:f.color, display:"inline-flex" }}><f.Icon size={12} /></span>{f.label}</span>
                <span style={S.countValue(f.color)}>{loading ? "…" : counts.c[f.key]}</span>
                {f.sub && !loading && counts.sub[f.key] > 0 && (
                  <span style={S.countSub}>⚡ {counts.sub[f.key]} due today</span>
                )}
              </button>
            );
          })}
        </div>

        {/* grouped table */}
        <div style={S.card}>
          {loading ? (
            <div style={S.empty}>Loading Test &amp; Tag register…</div>
          ) : groups.length === 0 ? (
            <div style={S.empty}>No items for this view.</div>
          ) : (
            <div style={{ maxHeight:420, overflowY:"auto", overflowX:"auto" }}>
              <div style={{ minWidth:820 }}>
                {groups.map((g) => (
                  <div key={g.date || "nodate"}>
                    <div style={S.dateHead}>
                      <span style={S.dateTitle}>
                        Unit Due · {fmtDate(g.date)}{g.dueToday > 0 ? ` · ⚡ ${g.dueToday} due today` : ""}
                      </span>
                      <span style={S.dateMeta}>{g.rows.length} item{g.rows.length === 1 ? "" : "s"}</span>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:TABLE_COLS, padding:"4px 8px 8px",
                      columnGap:8, borderBottom:"1px solid rgba(255,255,255,0.12)" }}>
                      {["Client","Item","Lead Status","Unit Status","Lead Due","Unit Due",""].map((h, i) => (
                        <span key={i} style={{ ...S.colHead, textAlign: i >= 2 && i <= 5 ? "center" : "left" }}>{h}</span>
                      ))}
                    </div>
                    {g.rows.map((r, i) => (
                      <div key={r.id || `${g.date}-${i}`} className="tnt-row"
                        style={{ display:"grid", gridTemplateColumns:TABLE_COLS, alignItems:"center",
                          padding:"9px 8px", columnGap:8, borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                        <span style={S.name}>{isDueToday(r) ? "⚡ " : ""}{r.client || "—"}</span>
                        <span style={S.cell}>{r.item || "—"}</span>
                        <span style={{ textAlign:"center" }}><span style={S.badge(eqI(r.leadStatus, "Ok"))}>{r.leadStatus || "—"}</span></span>
                        <span style={{ textAlign:"center" }}><span style={S.badge(eqI(r.unitStatus, "Ok"))}>{r.unitStatus || "—"}</span></span>
                        <span style={{ ...S.cell, textAlign:"center", color: r.leadDueISO === todayISO() ? "#fbbf24" : "#e2e8f0" }}>{fmtDate(r.leadDueISO)}</span>
                        <span style={{ ...S.cell, textAlign:"center", color: r.unitDueISO === todayISO() ? "#fbbf24" : "#e2e8f0" }}>{fmtDate(r.unitDueISO)}</span>
                        {/* every filter this row matches, as its icon in its colour */}
                        <span style={{ display:"flex", justifyContent:"flex-end", gap:5 }}>
                          {FILTERS.filter((f) => f.test(r)).map((f) => (
                            <span key={f.key} style={S.flag(f.color)} title={f.label}><f.Icon /></span>
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {!inline && <button type="button" style={S.closeBtn} onClick={onClose}>Close</button>}
        {sendOpen && (
          <SendModal filterHeader={filterHeader} reportRows={reportRows} summary={summary} onClose={() => setSendOpen(false)} />
        )}
      </div>
    </>
  );
  return inline ? shell : <div style={S.backdrop} onClick={onClose}>{shell}</div>;
};

export default TestNTagDashboard;